import type React from 'react';
import type Canvas from '../../objects/Canvas';
import { proxyCanvas } from './CanvaSXRenderingContext2D';
import { renderText } from './textRendering';
import { isElement, isInternalComponent, measuringElement } from './util';
import { type Properties } from 'csstype';
import { defaults, pick } from 'lodash';

const INTRINSIC_PROPS = ['flexGrow'] as const;

export interface MountedObject extends RectStruct {
  hoveredKey?: InteractiveKey;
  onClick?: (event) => void;
  onMouseOver?: (event) => void;
  onMouseOut?: (event) => void;
  onMouseMove?: (event) => void;
  tooltip?: React.ReactNode | ((key: InteractiveKey) => React.ReactNode);
  cursor?: Properties['cursor'];
}

export type MountedObjects = Record<InteractiveKey, MountedObject>;

export const isTestEnv =
  typeof process !== 'undefined' && process.env.NODE_ENV === 'test';

export function CanvaSX(
  type: CanvaSXComponent,
  props: ElementProps | null,
  ...children: CanvaSXNode[]
): CanvaSXElement {
  if (type.defaultProps) {
    props = { ...type.defaultProps, ...props };
  }

  if (children.length === 0 && props?.children) {
    children = Array.isArray(props.children)
      ? props.children
      : [props.children];

    delete props.children;
  }

  return {
    type,
    props: { ...props, children: (children || []).flat() },
    key: props?.key || null,
  };
}

export interface CanvaSXObject {
  // Stores references to any interactive CanvaSX components rendered on the object
  mountedObjects: MountedObjects;
}

interface InternalCanvaSXContext {
  // Internal context
  _canvasComputedStyle?: CSSStyleDeclaration;
  _cssVariables: Record<string, string>;
}

export interface CanvaSXContext {
  // The Fabric Canvas
  canvas: Canvas;

  // The 2d context, using our class override to track coordinates/scale
  ctx: ReturnType<typeof proxyCanvas>;

  // The object being rendered
  object: CanvaSXObject;

  // The scale to apply to all rendering
  scale: number;
}

CanvaSX.context = null as unknown as CanvaSXContext & InternalCanvaSXContext;

// Creates a CanvasX context which is required for rendering elements.
// Wrap your CanvasX code with this.
export function withContext(
  context: Omit<CanvaSXContext, 'ctx'> & {
    ctx: CanvasRenderingContext2D;
  },
  fn: (CanvasRenderingContext2D) => void
) {
  const prevContext = CanvaSX.context;
  const { ctx, scale = 1 } = context;

  const proxiedContext = proxyCanvas(ctx);

  // @ts-expect-error The wrapped SVG canvas puts the canvas element on __canvas, not canvas
  const canvasElement: HTMLCanvasElement = ctx.__canvas || ctx.canvas;
  let _canvasComputedStyle: InternalCanvaSXContext['_canvasComputedStyle'];

  if (!isTestEnv && canvasElement instanceof HTMLCanvasElement) {
    _canvasComputedStyle = getComputedStyle(canvasElement);
  }

  CanvaSX.context = {
    ...context,
    ctx: proxiedContext,
    _cssVariables: {},
    _canvasComputedStyle,
  };

  try {
    proxiedContext.save();
    proxiedContext.scale(scale, scale);

    // Set default fill style (for text)
    proxiedContext.fillStyle = 'black';
    proxiedContext.textBaseline = 'top';
    proxiedContext.font = '10px Arial';

    fn(proxiedContext);
  } finally {
    proxiedContext.restore();

    CanvaSX.context = prevContext;
  }
}

/**
 * This function converts and reduces non-internal JSX <Element>s into internal elements.
 * This is done to normalize children of other components/elements down to their simplest
 * implementation.
 *
 * This is mainly used for <FlexRow>. Let's say you have custom CanvaSX components like
 * this:
 *
 * const TextComponent = ({ text }) => <Text size={20}>{text}</Text>;
 * const NullComponent = () => null;
 *
 * And then a <FlexRow> like this:
 *
 * <FlexRow gap={10}>
 *   <TextComponent text="Test" />
 *   <NullComponent />
 *   <TextComponent text="Test" />
 * </FlexRow>
 *
 * There are two problems:
 *
 * 1. The `<FlexRow>`` will not see the `<Text>` child inside the `<TextComponent>`, so it
 *    cannot wrap the text properly, it will wrap the entire component as a single block.
 *
 * 2. The `<NullComponent>` will "render" as a child, but since it's empty, it will not
 *    show anything. However, the `gap` will be applied around it, leading to invalid
 *    rendering.
 *
 * So this will normalize the nodes to that the `<FlexRow>` looks more like this:
 *
 * <FlexRow gap={10}>
 *   <Text>Test</Text>
 *   {null}
 *   <Text>Test</Text>
 * </FlexRow>
 *
 * `<FlexRow>` then removes empty children before applying the wrapping/gap layout.
 */
export function normalizeNode(node: CanvaSXNode) {
  if (Array.isArray(node)) {
    return node.map(child => normalizeNode(child));
  }

  // If it's an element, we want to reduce/render it to a basic render-able element. Any
  // element that defines calculateDimensions is considered a base/low-level element.
  if (isElement(node)) {
    if (isInternalComponent(node)) {
      return node;
    }

    if (!node.type.calculateDimensions) {
      const intrinsicProps = pick(node.props, ...INTRINSIC_PROPS);
      const content = node.type(node.props);

      if (isElement(content)) {
        defaults(content.props, intrinsicProps);
      }

      // Recursively normalize the node, in case it's another component returning content
      return normalizeNode(content);
    }
  }

  return node;
}

/**
 * Clones an element, creating a copy of its props, and optionally deeply cloning its children
 */
export function cloneElement<T extends CanvaSXElement<any>>(
  element: T,
  deep = false
): T {
  const cloned = {
    key: element.key,
    type: element.type,
    props: { ...element.props },
  } as T;

  if (deep && 'children' in cloned.props) {
    cloned.props.children = cloneNode(cloned.props.children as CanvaSXNode);
  }

  return cloned;
}

/**
 * This is used to clone a node. This is primarily for elements, which will create copies
 * of the props, and also deep clone any children (if deep cloning).
 */
export function cloneNode(node: CanvaSXNode, deep = false): CanvaSXNode {
  if (isElement(node)) {
    return cloneElement(node, deep);
  }

  return node;
}

// Renders the CanvasXElement to the current context.
export function renderElement(element: CanvaSXElement) {
  const { type, props, key } = element;
  const { ctx, object } = CanvaSX.context;
  const { mountedObjects } = object;

  // This is a bit of a hack to avoid rendering while measuring. Perhaps we need a
  // completely separate layout/measuring step that executes before rendering, but
  // currently they two are tightly coupled.
  //
  // A CanvasXElement can return another CanvasXElement which we have to
  // recursively render.
  const content = type(props);
  if (content && !measuringElement) {
    renderElement(content);
  }

  if (
    key &&
    ('_dimensions' in props || ('width' in props && 'height' in props))
  ) {
    const dimensions = props._dimensions
      ? { width: props._dimensions.width, height: props._dimensions.height }
      : { width: props.width, height: props.height };

    const { x = 0, y = 0 } = props;

    mountedObjects[key] = {
      x: (ctx.position.x + x) * ctx.currentScaleStruct.scaleX,
      y: (ctx.position.y + y) * ctx.currentScaleStruct.scaleY,
      width: dimensions.width * ctx.currentScaleStruct.scaleX,
      height: dimensions.height * ctx.currentScaleStruct.scaleY,
      ...pick(
        props,
        'hoveredKey',
        'onClick',
        'onMouseOver',
        'onMouseMove',
        'onMouseOut',
        'tooltip',
        'cursor'
      ),
    };
  }
}

export function renderNode(node: CanvaSXNode) {
  switch (typeof node) {
    case 'string':
    case 'number':
      renderText(CanvaSX.context.ctx, String(node));
      break;
    case 'boolean':
      if (!node) {
        return;
      }
      renderText(CanvaSX.context.ctx, String(node));
      break;
    case 'object':
      if (node === null) {
        return;
      }
      if (Array.isArray(node)) {
        renderChildren(node);
      } else {
        renderElement(node);
      }
      break;

    case 'undefined':
      break;

    default:
      throw new Error(`unknown node type ${typeof node}`);
  }
}

export function renderChildren(children?: CanvaSXNode | CanvaSXNode[]) {
  if (Array.isArray(children)) {
    children.forEach(child => renderNode(child));
  } else if (typeof children !== 'undefined') {
    renderNode(children);
  }
}
